// Lib
import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
// Api
import { useGetInventoryItemDetailsQuery } from "rtkQuery/query/inventoryAPI";
// Hooks
import { useNotification, useTable, useViewport } from "hooks";
// Types
import { AdditionalFilter, ETable } from "types/tableFilters";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon } from "icons";
// Components
import { ArrowBackLink, Pagination, Table } from "components";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";

import { columns, filtersList } from "./config";

export const InventoryDetails: FC = () => {
  const { isDesktop } = useViewport();
  const { locationId, menuId, id } = useParams();

  const { openNotification } = useNotification();

  const {
    orderDirection,
    orderBy,
    currentPage,
    pageSize,
    debouncedFiltersQuery,
    selectedFilters,
    handleSort,
    setCurrentPage,
    setPageSize,
    handleSetTableFilterValues,
  } = useTable({ name: ETable.InventoryDetails });

  const {
    data: tableData,
    isFetching,
    error,
  } = useGetInventoryItemDetailsQuery(
    {
      id,
      menuId,
      query: {
        ...debouncedFiltersQuery,
        page: currentPage,
        size: pageSize,
        orderBy: orderBy,
        orderDirection: orderDirection,
      },
    },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
    }
  }, [error]);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    handleSort(sorter?.columnKey, sorter?.order);
  };

  return (
    <PageWrapper $fullwidth $column>
      <FlexContainer
        $fullwidth
        $column
        $gap={20}
        $grow={1}
        $padding={isDesktop && "0 0 32px"}
      >
        <FlexContainer $column $fullwidth $gap={16} $align="flex-start">
          <ArrowBackLink
            title="Back to Inventory"
            path="/loop/inventory/"
            state={{
              locationIdValue: locationId,
              menuIdValue: menuId,
            }}
          />

          <Typography.H1>
            {tableData?.item?.product?.name &&
              `${tableData?.item?.product?.name} - Changes History`}
          </Typography.H1>
        </FlexContainer>

        <Table
          isLoading={isFetching}
          header={{
            totalCount: tableData?.total,
            filter: {
              include: [AdditionalFilter.Users],
              filters: filtersList,
              selected: selectedFilters || [],
              setValue: handleSetTableFilterValues,
            },
          }}
          empty={{
            icon: DescriptionBlackIcon,
            title: "No records to show.",
            description: "Try changing sections or change the filters",
          }}
          columns={columns}
          dataSource={tableData?.data || []}
          onChange={handleTableChange}
        />
      </FlexContainer>

      <Pagination
        onPage
        padding="12px 16px"
        pageSize={pageSize}
        currentPage={currentPage}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        totalItems={tableData?.total}
      />
    </PageWrapper>
  );
};
